import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAllowedPublisherForProjects } from 'hooks/useAllowedPublisherForProjects';
import { buildQueryParams } from 'utils/queryParamsUtil';

import { GetSubscriptionResponse } from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

interface SubscriptionsFilters {
  pageSize?: number;
  offset?: number;
  status?: string;
  billingPeriodUnit?: string;
  planDisplayName?: string;
  customerEmail?: string;
  subscriptionId?: string;
}

export const DEFAULT_SUBSCRIPTIONS_FILTERS: SubscriptionsFilters = {
  pageSize: 10,
  offset: 0
};

export default function useSubscriptions(filters: SubscriptionsFilters = {}) {
  const mergedFilters = { ...DEFAULT_SUBSCRIPTIONS_FILTERS, ...filters };
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { allowedProjects } = useAllowedPublisherForProjects();

  const getSubscriptions = useQuery<GetSubscriptionResponse>({
    ...fetchConfig.general,
    queryKey: [
      EQueryKeys.SUBSCRIPTIONS,
      allowedProjects.join(','),
      mergedFilters
    ],
    queryFn: async () => {
      const subscriptions = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.SUBSCRIPTIONS)}?${buildQueryParams(mergedFilters)}`,
        {},
        allowedProjects.length
          ? { 'x-publishers': allowedProjects.join(',') }
          : undefined
      );
      return subscriptions;
    },
    enabled: !!allowedProjects.length
  });

  const cancelSubscription = useMutation({
    mutationFn: async (data: {
      subscriptionId: string;
      publisherId: string;
    }): Promise<any> => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.SUBSCRIPTIONS)}/${data.subscriptionId}/cancel`,
        {},
        data.publisherId ? { 'x-publishers': data.publisherId } : undefined
      );

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTIONS, allowedProjects.join(',')]
      });

      return response.data;
    }
  });

  return {
    getSubscriptions,
    cancelSubscription
  };
}
